import { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import { Banner, Box, Spinner, Toast, useToast } from '@zillow/constellation-10';

import { useQuery } from '@zg-rentals/react-query5';
import {
  AdproductsConstants,
  BoostWidget,
  getDefaultRentStartingAt,
  isRentStartingAt,
} from '@zg-rentals/rental-revenue-components';
import { getClient } from '@zg-rentals/ts-client-http-client';
import {
  BoostAdFlagType,
  RentalManagerUserAnalyticsBoostPreviewWebApiHandler_getV2,
  RentalManagerUserAnalyticsBoostPreviewWebApiHandler_getV2_Path,
} from '@zg-rentals/ts-rental-manager-api-client';
import {
  useRentalManagerUserPropertyBoostAdsWebApiHandler_getBoostListingDetails,
  useRentalManagerUserPropertyBoostAdsWebApiHandler_getListingHousingFlagTypes,
  useRentalManagerUserPropertyBoostAdsWebApiHandler_updateBoostListingDetails,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { DatarouterError } from '@zg-rentals/web-base';
import { debounce } from '@zg-rentals/zdash';

import { useGetListingDetails } from '../../../../hooks/queries/properties';
import { usePropertyEditMutation } from '../../../../hooks/usePropertyEditMutation';

import { useAuth } from '../../../AuthProvider';
import type { ZillowAdsFormValues } from '@zg-rentals/rental-revenue-components';
import type {
  GetUserPropertyDetailsPhotoWebDto,
  RentalManagerUpdateBoostListingDetailsRequestDto,
} from '@zg-rentals/ts-rental-manager-api-client';

type SponsoredAdsContentProps = {
  onOpenBuildingMedia: () => void;
};

export const PACKAGES_WITH_META_ADS = [
  AdproductsConstants.ProductCodes.PREMIUM_PACKAGE,
  AdproductsConstants.ProductCodes.PREMIUM_PLUS_PACKAGE,
  AdproductsConstants.ProductCodes.SIGNATURE_PACKAGE,
];

export const SponsoredAdsContent = ({ onOpenBuildingMedia }: SponsoredAdsContentProps) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;
  const { user } = useAuth();
  const { enqueueToast } = useToast();

  const [zillowAdFormValues, setZillowAdFormValues] = useState<ZillowAdsFormValues>({
    adFlag: BoostAdFlagType.NONE,
    customFlag: '',
    displayRentStartingAt: true,
    subheader: '',
    websiteUrl: '',
    customDetails: '',
    adCopy: '',
  });
  const [zillowAdFormErrors, setZillowAdFormErrors] = useState(new Map<keyof ZillowAdsFormValues, string>());
  const [dirtyZillowAdsFields, setDirtyZillowAdsFields] = useState(new Map<keyof ZillowAdsFormValues, boolean>());
  const [displayFairHousingError, setDisplayFairHousingError] = useState(false);

  const { data: listingDetails, isLoading: isFetchingListingDetails } = useGetListingDetails(listingAlias);

  const customerId = listingDetails?.customerId;
  const isFeed = listingDetails?.isFeed;
  const { city = '', state = '', street = '', propertyPhotos = [], fullDescription = '' } = listingDetails ?? {};
  const buildingName = listingDetails?.buildingAttributes?.buildingName;
  const productTypes = listingDetails?.adproduct?.productTypes || [];

  const address = `${street} ${city}, ${state}`;

  const {
    isLoading: isFetchingBoostListingDetails,
    data: boostListingDetails,
    error: getListingDetailsError,
  } = useRentalManagerUserPropertyBoostAdsWebApiHandler_getBoostListingDetails(
    { customerId: customerId ?? '', listingAlias },
    {
      enabled: Boolean(customerId && listingAlias),
      meta: {
        tags: ['boostListingDetails', listingAlias],
        context: {
          api: 'getBoostListingDetails',
          details: { customerId, listingAlias, userEmail: user?.email },
        },
      },
    },
  );

  const shouldValidateMetaAds =
    productTypes.some((pt: string) => PACKAGES_WITH_META_ADS.includes(pt)) || boostListingDetails?.facebookReady;

  useEffect(() => {
    if (boostListingDetails) {
      const customDetails =
        isRentStartingAt(boostListingDetails.otherDetails) || !boostListingDetails.otherDetails
          ? ''
          : boostListingDetails.otherDetails;

      setZillowAdFormValues({
        adFlag: boostListingDetails.adFlagCustomText
          ? BoostAdFlagType.CUSTOM
          : (boostListingDetails.adFlag ?? BoostAdFlagType.NONE),
        subheader: boostListingDetails.subheader ?? '',
        customDetails,
        customFlag: boostListingDetails.adFlagCustomText ?? '',
        displayRentStartingAt: !customDetails,
        websiteUrl: boostListingDetails.adTargetUrl ?? '',
        adCopy: boostListingDetails.adCopy ?? '',
      });
    }
  }, [boostListingDetails]);

  const { data: boostAdPreviews, isLoading: isFetchingBoostAdPreviews } = useQuery({
    queryKey: [RentalManagerUserAnalyticsBoostPreviewWebApiHandler_getV2_Path, { customerId, listingAlias }],
    queryFn: async () => {
      const response = await RentalManagerUserAnalyticsBoostPreviewWebApiHandler_getV2({
        insertionOrderId: boostListingDetails!.insertionOrderId.toString(),
      });

      // Auto-populate subheader if not set
      if (!zillowAdFormValues.subheader) {
        setZillowAdFormValues({
          ...zillowAdFormValues,
          subheader: response?.dfpPreview?.templateValues?.TextSubheader01 ?? '',
        });
      }

      return response;
    },
    enabled: !!boostListingDetails?.insertionOrderId,
  });

  const {
    isLoading: isFetchingHousingFlags,
    data: housingFlagTypes,
    error: housingFlagTypesError,
  } = useRentalManagerUserPropertyBoostAdsWebApiHandler_getListingHousingFlagTypes(
    { listingAlias },
    {
      enabled: Boolean(listingAlias),
      meta: {
        tags: ['housingFlagTypes', listingAlias],
        context: {
          api: 'getListingHousingFlagTypes',
          details: { listingAlias, userEmail: user?.email },
        },
      },
    },
  );

  const { mutateAsync: updateBoostListingDetails, isPending: isUpdatingBoostListingDetails } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyBoostAdsWebApiHandler_updateBoostListingDetails,
    httpClient: getClient().extend({ timeout: 30000 }),
    successToastMessage: 'Sponsored ads updated',
    onError: (error: unknown) => {
      const errorMessage = DatarouterError.getDisplayMessage(error);

      // Handle specific error cases
      if (errorMessage.includes('The format of the URL')) {
        const newErrors = new Map<keyof ZillowAdsFormValues, string>();
        newErrors.set('websiteUrl', 'Invalid format for url');
        setZillowAdFormErrors(newErrors);
      } else if (errorMessage.includes('not fair housing compliant')) {
        setDisplayFairHousingError(true);
      } else {
        enqueueToast(<Toast tone="critical">{errorMessage ?? 'Sponsored ad couldn’t be updated. Try again.'}</Toast>);
      }
    },
    meta: {
      invalidates: ['boostListingDetails', 'boostAdPreviews'],
      context: {
        api: 'updateBoostListingDetails',
        details: { customerId, listingAlias, userEmail: user?.email },
      },
    },
  });

  const propertyPhotosURls: Array<string> = propertyPhotos?.map((p: GetUserPropertyDetailsPhotoWebDto) => p.url) || [];

  const markEverythingAsDirty = (overrideDirtyFields?: Map<keyof ZillowAdsFormValues, boolean>) => {
    const newDirtyFields = new Map(overrideDirtyFields || dirtyZillowAdsFields);

    Object.keys(zillowAdFormValues).forEach((key) => {
      if (key !== 'subheader') newDirtyFields.set(key as keyof ZillowAdsFormValues, true);
    });

    setDirtyZillowAdsFields(newDirtyFields);
  };

  const debouncedUpdateBoostAds = useCallback(
    debounce(async (formFields: ZillowAdsFormValues) => {
      if (!boostListingDetails) return;

      const postData: RentalManagerUpdateBoostListingDetailsRequestDto = {
        adFlag: formFields.adFlag,
        adCopy: formFields.adCopy ? formFields.adCopy.trim() : '',
        adFlagCustomText: formFields.adFlag === BoostAdFlagType.CUSTOM ? formFields.customFlag.trim() : '',
        adTargetUrl: formFields.websiteUrl.trim(),
        insertionOrderId: boostListingDetails.insertionOrderId,
        creativeId: boostListingDetails.creativeId,
        otherDetails: !formFields.displayRentStartingAt
          ? formFields.customDetails.trim()
          : getDefaultRentStartingAt(boostListingDetails.lowPrice),
        subheader: formFields.subheader ? formFields.subheader.trim() : '',
      };

      await updateBoostListingDetails({ postData });
    }, 5000),
    [boostListingDetails, updateBoostListingDetails],
  );

  const onChangeForm = (
    formFields: ZillowAdsFormValues,
    errors?: Map<keyof ZillowAdsFormValues, string>,
    _metaErrors?: Map<keyof ZillowAdsFormValues, string>,
    newDirtyFields?: Map<keyof ZillowAdsFormValues, boolean>,
  ) => {
    setZillowAdFormValues(formFields);

    const isZillowCorrect =
      (errors && errors?.size <= 0) ||
      (errors?.size === 1 && errors.has('subheader') && !newDirtyFields?.get('subheader'));

    markEverythingAsDirty(newDirtyFields);
    setDisplayFairHousingError(false);

    if (!isZillowCorrect || !boostListingDetails) return;

    debouncedUpdateBoostAds(formFields);
  };

  useEffect(() => {
    return () => debouncedUpdateBoostAds.cancel();
  }, [debouncedUpdateBoostAds]);

  const openAndScrollIntoBuildingMedia = () => {
    onOpenBuildingMedia();

    requestAnimationFrame(() => {
      const buildingMediaAccordion = document.querySelector('[data-accordion-index="2"]');
      buildingMediaAccordion?.scrollIntoView({ behavior: 'smooth' });
    });
  };

  if (
    isFetchingListingDetails ||
    isFetchingBoostListingDetails ||
    isFetchingHousingFlags ||
    isFetchingBoostAdPreviews
  ) {
    return (
      <Box css={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Spinner size="lg" />
      </Box>
    );
  }

  if (propertyPhotosURls.length <= 0) {
    return <Banner tone="critical">This property does not have images</Banner>;
  }

  if (getListingDetailsError || !boostListingDetails || housingFlagTypesError || !housingFlagTypes) {
    return <Banner tone="critical">Unexpected error loading sponsored ads</Banner>;
  }

  const googleAdPreview = boostAdPreviews?.dfpPreview?.templateValues?.ImageBrandRectangle ?? propertyPhotosURls[0];

  return (
    <Box css={{ position: 'relative', margin: '{spacing.-400}' }}>
      <BoostWidget
        title={buildingName || ''}
        zillowAdPreviewProps={{
          imageSrc: googleAdPreview,
          rentStartingAtPrice: boostListingDetails.lowPrice ?? 0,
          additionalBoostAdFlagOptions: housingFlagTypes,
        }}
        zillowAdFormValues={zillowAdFormValues}
        setZillowAdFormValues={onChangeForm}
        zillowAdFormErrors={zillowAdFormErrors}
        setZillosAdFormErrors={setZillowAdFormErrors}
        metaAdPreviewProps={{
          previewImages: propertyPhotosURls,
        }}
        disabled={isUpdatingBoostListingDetails}
        shouldValidateMetaAds={shouldValidateMetaAds}
        displayMediaMessage={!isFeed}
        address={address}
        description={fullDescription || ''}
        onClickSeeBuildingMedia={openAndScrollIntoBuildingMedia}
        hideAdFlagOptions={[]}
        dirtyZillowAdsFields={dirtyZillowAdsFields}
        setDirtyZillowAdsFields={setDirtyZillowAdsFields}
        displayFairHousingError={displayFairHousingError}
      />
      {isUpdatingBoostListingDetails && (
        <Box
          css={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Spinner size="lg" />
        </Box>
      )}
    </Box>
  );
};
